import {
    useContext,
    useEffect,
    useState,
    Fragment
} from "react";
import {
    View
} from "react-native";
import type IEditProfileModalProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitSnackBar,
    NCoreUIKitTheme,
    TextInput,
    Button,
    Modal,
    Text
} from "ncore-ui-kit";
import {
    SERVICE_EVENT_TYPES
} from "../../../../services/restAPI/constants";
import {
    AuthContext
} from "../../../../contexts/AuthContext";
import RESTServiceContext from "../../../../services/restAPI/context";

const EditProfileModal = ({
    initialData,
    isVisible,
    onSuccess,
    onClose
}: IEditProfileModalProps) => {
    const {
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        updateUser
    } = useContext(AuthContext);

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState<boolean>(false);

    const [
        phoneNumber,
        setPhoneNumber
    ] = useState<string>(initialData?.phoneNumber || "");

    const [
        firstName,
        setFirstName
    ] = useState<string>(initialData?.firstName || "");

    const [
        lastName,
        setLastName
    ] = useState<string>(initialData?.lastName || "");

    useEffect(() => {
        if (isVisible && initialData) {
            setPhoneNumber(initialData.phoneNumber || "");
            setFirstName(initialData.firstName || "");
            setLastName(initialData.lastName || "");
        }
    }, [
        initialData,
        isVisible
    ]);

    const handleResetStates = () => {
        if (initialData) {
            setPhoneNumber(initialData.phoneNumber || "");
            setFirstName(initialData.firstName || "");
            setLastName(initialData.lastName || "");
        }
        onClose();
    };

    const handleUpdateProfile = async () => {
        const trimmedPhone = phoneNumber.trim();
        const trimmedFirst = firstName.trim();
        const trimmedLast = lastName.trim();

        if (!trimmedFirst && !trimmedLast && !trimmedPhone) {
            NCoreUIKitSnackBar.open({
                title: localize("fill-all-fields")
            });
            return;
        }

        const payload: {
            phoneNumber?: string;
            firstName?: string;
            lastName?: string;
        } = {};

        if (trimmedFirst && trimmedFirst !== initialData?.firstName) {
            payload.firstName = trimmedFirst;
        }

        if (trimmedLast && trimmedLast !== initialData?.lastName) {
            payload.lastName = trimmedLast;
        }

        if (trimmedPhone && trimmedPhone !== initialData?.phoneNumber) {
            payload.phoneNumber = trimmedPhone;
        }

        if (Object.keys(payload).length === 0) {
            NCoreUIKitSnackBar.open({
                title: "Herhangi bir değişiklik yapmadınız."
            });
            return;
        }

        try {
            setIsSubmitting(true);
            const result = await RESTServiceContext.action("updateProfile", payload);

            if (result.code === 200) {
                const finalFirstName = payload.firstName || initialData?.firstName || "";
                const finalLastName = payload.lastName || initialData?.lastName || "";
                const finalFullName = `${finalFirstName} ${finalLastName}`.trim();
                const finalPhone = payload.phoneNumber || initialData?.phoneNumber;

                await updateUser({
                    firstName: finalFirstName,
                    lastName: finalLastName,
                    phoneNumber: finalPhone,
                    fullName: finalFullName
                });

                RESTServiceContext.emitWithKey(SERVICE_EVENT_TYPES.USER_UPDATED, {
                    firstName: finalFirstName,
                    lastName: finalLastName,
                    phoneNumber: finalPhone,
                    fullName: finalFullName
                });

                NCoreUIKitSnackBar.open({
                    title: result.message || "Profil başarıyla güncellendi."
                });

                onSuccess?.();
                onClose();
            } else {
                NCoreUIKitSnackBar.open({
                    title: result.message || localize("something-went-wrong")
                });
            }
        } catch (err) {
            console.error(err);
            NCoreUIKitSnackBar.open({
                title: localize("something-went-wrong")
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return <Fragment>
        <Modal
            onClosed={handleResetStates}
            portalName="modal-system"
            id="edit-profile-modal"
            isActive={isVisible}
        >
            <View
                style={[
                    stylesheet.container,
                    {
                        backgroundColor: colors.project.brandWhite,
                        padding: spaces.spacingXl,
                        borderRadius: radiuses.lg,
                        gap: spaces.spacingXl
                    }
                ]}
            >
                <View
                    style={stylesheet.headerContainer}
                >
                    <Text
                        variant="titleLargeSize"
                    >
                        {localize("edit-profile-info") || "Profil Bilgilerini Düzenle"}
                    </Text>
                </View>
                <View
                    style={[
                        stylesheet.formContainer,
                        {
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <TextInput
                        placeholder={localize("members-form-firstname")}
                        title={localize("members-form-firstname")}
                        onChangeText={setFirstName}
                        spreadBehaviour="stretch"
                        value={firstName}
                    />
                    <TextInput
                        placeholder={localize("members-form-lastname")}
                        title={localize("members-form-lastname")}
                        onChangeText={setLastName}
                        spreadBehaviour="stretch"
                        value={lastName}
                    />
                    <TextInput
                        placeholder={localize("members-form-phone")}
                        title={localize("members-form-phone")}
                        onChangeText={setPhoneNumber}
                        spreadBehaviour="stretch"
                        value={phoneNumber}
                    />
                </View>
                <View
                    style={[
                        stylesheet.footerContainer,
                        {
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Button
                        customBorderColor="secondaryButton"
                        customColor="secondaryButton"
                        onPress={handleResetStates}
                        title={localize("cancel")}
                        variant="filled"
                    />
                    <Button
                        onPress={handleUpdateProfile}
                        title={localize("save")}
                        isLoading={isSubmitting}
                    />
                </View>
            </View>
        </Modal>
    </Fragment>;
};

export default EditProfileModal;
